import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { LeaseholderStats, CommunityStats } from '../types/lease';

export function useDashboardStats() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [leaseholderStats, setLeaseholderStats] = useState<LeaseholderStats[]>([]);
  const [communityStats, setCommunityStats] = useState<CommunityStats[]>([]);

  useEffect(() => {
    async function fetchStats() {
      try {
        setLoading(true);
        setError(null);

        // Get all lease data from lot_lease_summary
        const { data: leases, error: leaseError } = await supabase
          .from('lot_lease_summary')
          .select('*');

        if (leaseError) throw leaseError;

        // Calculate leaseholder statistics
        const holderStatsMap = (leases || []).reduce((acc: { [key: string]: LeaseholderStats }, lease) => {
          if (!lease.holder_name) return acc;
          
          if (!acc[lease.holder_name]) {
            acc[lease.holder_name] = {
              holder_name: lease.holder_name,
              lease_count: 0,
              total_value: 0
            };
          }
          acc[lease.holder_name].lease_count++;
          // Annualize the rental amount based on frequency
          const annualMultiplier = getAnnualMultiplier(lease.lease_rent_freq);
          acc[lease.holder_name].total_value += (lease.rental_amount || 0) * annualMultiplier;
          return acc;
        }, {});

        // Calculate community statistics
        const communityStatsMap = (leases || []).reduce((acc: { [key: string]: CommunityStats }, lease) => {
          if (!lease.community_name) return acc;

          if (!acc[lease.community_name]) {
            acc[lease.community_name] = {
              comm_name: lease.community_name,
              active_leases: 0,
              available_lots: 0,
              total_value: 0
            };
          }
          if (lease.lease_end && new Date(lease.lease_end) >= new Date()) {
            acc[lease.community_name].active_leases++;
            // Annualize the rental amount based on frequency
            const annualMultiplier = getAnnualMultiplier(lease.lease_rent_freq);
            acc[lease.community_name].total_value += (lease.rental_amount || 0) * annualMultiplier;
          }
          return acc;
        }, {});

        // Get available lots count from land_lots table
        const { data: availableLots, error: lotsError } = await supabase
          .from('land_lots')
          .select('lot_community, lot_avail')
          .eq('lot_avail', 'Y');

        if (lotsError) throw lotsError;

        // First ensure all communities with available lots are in the stats map
        (availableLots || []).forEach(lot => {
          const communityName = lot.lot_community;
          if (!communityStatsMap[communityName]) {
            communityStatsMap[communityName] = {
              comm_name: communityName,
              active_leases: 0,
              available_lots: 0,
              total_value: 0
            };
          }
        });

        // Then update available lots count for all communities
        (availableLots || []).forEach(lot => {
          const communityName = lot.lot_community;
          communityStatsMap[communityName].available_lots++;
        });

        // Sort leaseholder stats by total value
        const sortedLeaseholderStats = Object.values(holderStatsMap)
          .sort((a, b) => b.total_value - a.total_value);

        // Sort community stats by active leases
        const sortedCommunityStats = Object.values(communityStatsMap)
          .sort((a, b) => b.active_leases - a.active_leases);

        setLeaseholderStats(sortedLeaseholderStats);
        setCommunityStats(sortedCommunityStats);
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch dashboard stats'));
      } finally {
        setLoading(false);
      }
    }

    fetchStats();
  }, []);

  return {
    loading,
    error,
    leaseholderStats,
    communityStats,
    refresh: async () => {
      setLoading(true);
      try {
        const { data: leases, error: leaseError } = await supabase
          .from('lot_lease_summary')
          .select('*');

        if (leaseError) throw leaseError;

        // Recalculate stats using the same logic as above
        const holderStatsMap = (leases || []).reduce((acc: { [key: string]: LeaseholderStats }, lease) => {
          if (!lease.holder_name) return acc;

          if (!acc[lease.holder_name]) {
            acc[lease.holder_name] = {
              holder_name: lease.holder_name,
              lease_count: 0,
              total_value: 0
            };
          }
          acc[lease.holder_name].lease_count++;
          const annualMultiplier = getAnnualMultiplier(lease.lease_rent_freq);
          acc[lease.holder_name].total_value += (lease.rental_amount || 0) * annualMultiplier;
          return acc;
        }, {});

        const communityStatsMap = (leases || []).reduce((acc: { [key: string]: CommunityStats }, lease) => {
          if (!lease.community_name) return acc;

          if (!acc[lease.community_name]) {
            acc[lease.community_name] = {
              comm_name: lease.community_name,
              active_leases: 0,
              available_lots: 0,
              total_value: 0
            };
          }
          if (lease.lease_end && new Date(lease.lease_end) >= new Date()) {
            acc[lease.community_name].active_leases++;
            const annualMultiplier = getAnnualMultiplier(lease.lease_rent_freq);
            acc[lease.community_name].total_value += (lease.rental_amount || 0) * annualMultiplier;
          }
          return acc;
        }, {});

        const { data: availableLots, error: lotsError } = await supabase
          .from('land_lots')
          .select('lot_community, lot_avail')
          .eq('lot_avail', 'Y');

        if (lotsError) throw lotsError;

        // First ensure all communities with available lots are in the stats map
        (availableLots || []).forEach(lot => {
          const communityName = lot.lot_community;
          if (!communityStatsMap[communityName]) {
            communityStatsMap[communityName] = {
              comm_name: communityName,
              active_leases: 0,
              available_lots: 0,
              total_value: 0
            };
          }
        });

        // Then update available lots count for all communities
        (availableLots || []).forEach(lot => {
          const communityName = lot.lot_community;
          communityStatsMap[communityName].available_lots++;
        });

        setLeaseholderStats(
          Object.values(holderStatsMap).sort((a, b) => b.total_value - a.total_value)
        );
        setCommunityStats(
          Object.values(communityStatsMap).sort((a, b) => b.active_leases - a.active_leases)
        );
        setError(null);
      } catch (err) {
        console.error('Error refreshing dashboard stats:', err);
        setError(err instanceof Error ? err : new Error('Failed to refresh dashboard stats'));
      } finally {
        setLoading(false);
      }
    }
  };
}

function getAnnualMultiplier(frequency: string | null): number {
  if (!frequency) return 1;
  
  switch (frequency.toLowerCase()) {
    case 'weekly':
      return 52;
    case 'fortnightly':
      return 26;
    case 'monthly':
      return 12;
    case 'quarterly':
      return 4;
    case 'annually':
      return 1;
    default:
      return 1;
  }
}